import { useQuery } from '@tanstack/react-query';
import { type ApiClient } from 'api/client';
import { CARDS_PER_PAGE } from 'constants/itemsPerPage';
import useApi from 'hooks/useApi';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import cardsFactory from 'utils/factories/card';

export type CardsFilters = Parameters<ApiClient['cards']['apiCardsGetCollection']>[0];

type useCardsApiProps = {
  filters?: Partial<CardsFilters>;
  enabled?: boolean;
  // In controlled mode the filters are not taken from the URL
  controlled?: boolean;
};

const useCardsApi = ({ filters, enabled = true, controlled = false }: useCardsApiProps) => {
  const apiClient = useApi();
  const { user } = useUserApi();
  const {
    query: { slug, page, ...query },
    locale,
  } = useRouter();

  const cleanedQuery = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== 'null'));

  const queryParams = {
    ...(filters || {}),
    ...(!!page && !controlled && { page: Number(page) }),
    ...(controlled ? {} : cleanedQuery),
    itemsPerPage: CARDS_PER_PAGE,
    locale: locale!,
  };

  const queryKey = cardsFactory.list(queryParams);
  const statsQueryKey = cardsFactory.statsList(queryParams);

  const cardsQuery = useQuery({
    queryKey,
    queryFn: () => apiClient.cards.apiCardsGetCollection(queryParams),
    select: (res) => res.data,
    enabled,
  });

  const statsQuery = useQuery({
    queryKey: statsQueryKey,
    queryFn: () => apiClient.cards.apiCardsstatsGetCollection(queryParams),
    select: (res) => res.data,
    enabled: !!user,
  });

  const cards = useMemo(
    () =>
      cardsQuery.data?.['hydra:member'].map((card, index) => {
        return {
          ...card,
          stats: statsQuery?.data?.['hydra:member'][index],
        };
      }) || [],
    [statsQuery.data, cardsQuery.data],
  );

  return {
    cards: cards || [],
    totalItems: cardsQuery.data?.['hydra:totalItems'],
    cardsQuery,
    statsQuery,
    cardsQueryKey: queryKey,
    statsQueryKey,
    isLoading: cardsQuery.isLoading || statsQuery.isLoading,
  };
};

export default useCardsApi;
